import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';
import styles from './audio-player.module.scss';
import { Flex, Slider, Row, Col } from 'antd';
import Dropdown from '../form/dropdown/dropdown';
import VolumeMaxIcon from '@/assets/icons/volume-max.svg?react';
import VolumeMinIcon from '@/assets/icons/volume-min.svg?react';
import VolumeXIcon from '@/assets/icons/volume-x.svg?react';
import Play from '@/assets/icons/play-circle.svg?react';
import Pause from '@/assets/icons/pause-circle.svg?react';
import FastForward from '@/assets/icons/fast-forward.svg?react';
import FastForward5 from '@/assets/icons/fast-forward-5-sec.svg?react';
import FastBackward from '@/assets/icons/fast-backward.svg?react';
import FastBackward5 from '@/assets/icons/rewind-5-sec.svg?react';
import Icon from '@ant-design/icons';
import RegionsPlugin from 'wavesurfer.js/dist/plugins/regions.js';
import clsx from 'clsx';
import { calculateSecondsToMinutes } from '@/utils/helpers';
import { AUDIO_SEEK_TIME_IN_S, PLAYBACK_SPEED_OPTIONS } from './audio-player.constants';
import Loader from '../loader/loader';
import { MaintenanceCard } from '../maintenance-card/maintenance-card';
import { Maintenance } from '@/services/maintenances-service/maintenances-service.types';

interface AudioPlayerProps {
  src: string;
  detectedKeywords?: { start: number; end: number }[];
  currentTime: string;
  setCurrentTime: (value: string) => void;
  duration: string;
  setDuration: (value: string) => void;
  shouldPlay?: boolean;
  isPlaying: boolean;
  setIsPlaying: Dispatch<SetStateAction<boolean>>;
  isFloating?: boolean;
  maintenances?: Maintenance[];
  /** Bar-wave height for the non-floating variant. 80 on a full page, 30 in the drawer. */
  waveHeight?: number;
  /** Dark panel treatment: ink background, round indigo play button, tabular time. */
  dark?: boolean;
}

export default function AudioPlayer({
  src,
  detectedKeywords = [],
  currentTime,
  setCurrentTime,
  duration,
  setDuration,
  isPlaying,
  setIsPlaying,
  shouldPlay,
  isFloating = false,
  maintenances,
  waveHeight = 80,
  dark = false,
}: AudioPlayerProps) {
  const waveAudioRef = useRef<WaveSurfer | null>(null);
  const audioContainerRef = useRef<HTMLDivElement>(null);
  const [volume, setVolume] = useState(1);
  const [isVolumePanelVisible, setIsVolumePanelVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isHoveredBackward, setIsHoveredBackward] = useState(false);
  const [isHoveredForward, setIsHoveredForward] = useState(false);

  const getVolumeIcon = () => {
    if (volume > 0.5) {
      return <VolumeMaxIcon />;
    } else if (volume > 0) {
      return <VolumeMinIcon />;
    } else {
      return <VolumeXIcon />;
    }
  };

  const getPlayIcon = () => {
    if (isPlaying) {
      return <Pause />;
    } else {
      return <Play />;
    }
  };

  const skipAudio = (value: number) => waveAudioRef.current?.skip(value);

  const toggleVolumePanel = () => {
    setIsVolumePanelVisible((prev) => !prev);
  };

  const handlePlayPause = () => {
    if (!isFloating) {
      waveAudioRef.current?.playPause();
      setIsPlaying(waveAudioRef.current?.isPlaying() ?? false);
    } else {
      setIsPlaying((prev) => !prev);
    }
  };

  useEffect(() => {
    if (audioContainerRef.current && src) {
      const waveform = WaveSurfer.create({
        container: audioContainerRef.current,
        // On the ink panel the light unplayed bars would vanish, so they go translucent
        // white and the played portion keeps the indigo fill.
        waveColor: dark ? styles.waveColorDark : styles.waveColor,
        progressColor: isFloating ? styles.progressColorFloating : styles.progressColor,
        url: src,
        dragToSeek: true,
        height: 32,
      });

      if (isFloating) {
        waveform.options.renderFunction = (_, ctx) => {
          const { width, height } = ctx.canvas;

          ctx.translate(0, height / 2);
          ctx.strokeStyle = ctx.fillStyle;
          ctx.fillStyle = 'white';
          ctx.roundRect(0, 0, width, 16, 12);
          ctx.fill();
          ctx.stroke();
        };
      } else {
        waveform.options.barGap = 3;
        waveform.options.barWidth = 4;
        waveform.options.barRadius = 8;
        waveform.options.barAlign = 'bottom';
        waveform.options.height = waveHeight;
      }

      waveform.on('ready', () => {
        setIsLoading(false);
      });

      waveform.on('audioprocess', (time) => {
        setCurrentTime(calculateSecondsToMinutes(time));
      });

      waveform.on('interaction', (time) => {
        setCurrentTime(calculateSecondsToMinutes(time));
      });

      waveform.on('seeking', (time) => {
        setCurrentTime(calculateSecondsToMinutes(time));
      });

      waveform.on('finish', () => {
        setIsPlaying(false);
      });

      waveAudioRef.current = waveform;
    }

    return () => {
      waveAudioRef.current?.destroy();
    };
  }, [isFloating, setCurrentTime, setIsPlaying, src]);

  useEffect(() => {
    waveAudioRef.current?.setVolume(volume);
  }, [volume]);

  useEffect(() => {
    const waveformRegions = waveAudioRef.current?.registerPlugin(RegionsPlugin.create());

    waveAudioRef.current?.on('decode', (duration) => {
      detectedKeywords.forEach(({ start, end }) => {
        waveformRegions?.addRegion({
          start: start,
          end: end,
          drag: false,
          resize: false,
        });
      });
      setDuration(calculateSecondsToMinutes(duration));
    });
  }, [detectedKeywords, setDuration]);

  useEffect(() => {
    if (!isLoading && shouldPlay) {
      if (isPlaying) {
        waveAudioRef.current?.play();
      } else {
        waveAudioRef.current?.pause();
      }
      setIsPlaying(waveAudioRef.current?.isPlaying() ?? false);
    }
  }, [isLoading, isPlaying, setIsPlaying, shouldPlay]);

  return (
    <>
      <div className={clsx(styles.playerCardWrapper, dark && styles.playerCardWrapperDark)}>
        <Flex vertical className={clsx(isFloating ? styles.playerFloating : styles.player)}>
          <div
            ref={audioContainerRef}
            className={clsx(isFloating ? styles.waveformFloating : styles.waveform)}
            id="waveform"
          >
            {isLoading && !isFloating && <Loader />}
          </div>
          <Flex
            className={clsx(isFloating && styles.duration, styles.timeRow)}
            justify="space-between"
          >
            <p>{currentTime}</p>
            <p>{duration}</p>
          </Flex>
        </Flex>

        {maintenances && maintenances.length > 0 && (
          <Flex className={styles.widgetWrapper}>
            <MaintenanceCard
              carouselClassName={styles.carousel}
              className={styles.maintenanceCard}
              maintenances={maintenances}
            />
          </Flex>
        )}
      </div>

      <Row
        align={isFloating ? undefined : 'middle'}
        className={clsx(isFloating ? styles.wrapperFloating : styles.wrapper)}
      >
        <Col
          span={isFloating ? undefined : 8}
          className={clsx(styles.gridCol, isFloating && styles['gridCol-3'])}
        >
          <Dropdown
            allowClear={false}
            className={styles.playbackSpeed}
            dropdownListClass={styles.playbackOptions}
            options={[...PLAYBACK_SPEED_OPTIONS]}
            defaultValue={PLAYBACK_SPEED_OPTIONS[0]}
            onChange={(value) => waveAudioRef.current?.setPlaybackRate(value)}
          />
        </Col>

        <Col
          span={isFloating ? undefined : 8}
          className={clsx(styles.gridCol, isFloating && styles['gridCol-1'])}
        >
          <Flex gap={16} justify="center" className={styles.controls}>
            <Icon
              className={styles.seekControls}
              data-testid="seekBackward"
              onClick={() => skipAudio(-AUDIO_SEEK_TIME_IN_S)}
              component={isHoveredBackward ? FastBackward5 : FastBackward}
              onMouseEnter={() => setIsHoveredBackward(true)}
              onMouseLeave={() => setIsHoveredBackward(false)}
            />
            <Icon
              className={styles.playPause}
              data-testid="playPause"
              onClick={handlePlayPause}
              component={getPlayIcon}
            />
            <Icon
              className={styles.seekControls}
              data-testid="seekForward"
              onClick={() => skipAudio(AUDIO_SEEK_TIME_IN_S)}
              component={isHoveredForward ? FastForward5 : FastForward}
              onMouseEnter={() => setIsHoveredForward(true)}
              onMouseLeave={() => setIsHoveredForward(false)}
            />
          </Flex>
        </Col>

        <Col
          span={isFloating ? undefined : 8}
          className={clsx(styles.gridCol, isFloating && styles['gridCol-2'])}
        >
          <Flex className={clsx(styles.volumeContainer, isFloating && 'isFloating')}>
            <Icon
              data-testid="volumeIcon"
              className={clsx(styles.volumeIcon, isVolumePanelVisible && styles.volumeIconActive)}
              component={getVolumeIcon}
              onClick={toggleVolumePanel}
            />
            {isVolumePanelVisible && (
              <Flex className={styles.volumeWrapper}>
                <Slider
                  className={styles.volumeSlider}
                  vertical
                  defaultValue={volume * 100}
                  step={10}
                  min={0}
                  max={100}
                  onChange={(value) => setVolume(value / 100)}
                />
              </Flex>
            )}
          </Flex>
        </Col>
      </Row>
    </>
  );
}
